/**
 * The page-guide data contract — pure data and pure functions.
 *
 * No React, no Fluent UI, no router, no jotai. `scripts/help-check.mjs`, the manual generator
 * (`scripts/build-user-manual.mjs`) and the Jest tests import this file and the registry
 * directly, so a UI import sneaking in here breaks all three. Keep it dependency-free.
 */

/**
 * The role ladder. HBH employee self-service has one tier today: every signed-in employee
 * sees the same screens, and what differs between them (a client without dependents, a package
 * that needs a salary-deduction consent) is DATA, expressed with `optional` / `context` on the
 * step, not with a role.
 *
 * Kept as a ladder anyway so a future tier (an HR viewer, say) is one line here plus one flag in
 * `HelpViewer`. Mirror what the SERVER enforces: a guide that shows a step the server would
 * refuse is worse than no step.
 */
export type HelpRole = "employee";

/**
 * The languages a guide may be written in, EN being the one it is always written in.
 *
 * The app's own languages (see `useLanguageOptions`) reduced to their language part —
 * `helpLanguageFor()` maps the i18next code (`de-AT`, `sr-Latn-RS`) onto this list. Declared here
 * rather than imported so this file stays importable from Node with no bundler.
 */
export const HELP_LANGUAGES = ["en", "de", "bg", "ro", "pl", "hu", "lt", "sr"] as const;
export type HelpLanguage = (typeof HELP_LANGUAGES)[number];
/** EN is the source text, never a translation — hence `i18n` covers only the others. */
export const HELP_BASE_LANGUAGE: HelpLanguage = "en";
export type HelpTranslatedLanguage = Exclude<HelpLanguage, "en">;

/**
 * The guide language for an i18next language code: `de-AT` → `de`, `sr-Latn-RS` → `sr`, anything
 * the guides are not written for → EN. The guide follows the app's language switch; it has no
 * language toggle of its own.
 */
export const helpLanguageFor = (appLanguage: string | undefined): HelpLanguage => {
  const prefix = (appLanguage ?? "").toLowerCase().split("-")[0];
  return HELP_LANGUAGES.find((code) => code === prefix) ?? HELP_BASE_LANGUAGE;
};

/**
 * Translations, per field and optional at every level.
 *
 * Per FIELD rather than per step, because a half-translated guide is the normal state of one.
 * Anything unset falls back to the EN text, and the surface says so (the `EN` pill) rather than
 * passing the fallback off as a translation.
 *
 * Selectors are NOT translatable: the anchor is the contract between guide and page, shared
 * across languages — which is why `help:check`, `help:impact` and the hooks are language-blind.
 */
export type HelpStepI18n = Partial<
  Record<HelpTranslatedLanguage, { title?: string; body?: string }>
>;

export type PageHelpI18n = Partial<
  Record<
    HelpTranslatedLanguage,
    {
      title?: string;
      intro?: string;
      /** EN section name → translated heading. Keyed by the EN name, which stays the id. */
      sections?: Record<string, string>;
      /** `HelpStep.context` key → the translated "how to get there" line. */
      contexts?: Record<string, string>;
      tips?: string[];
    }
  >
>;

/**
 * What the signed-in user is allowed to see — passed in as plain flags so `help/` stays
 * importable with no providers. Empty while there is a single tier; add a flag per new tier.
 */
export type HelpViewer = Record<string, never>;

export interface HelpStep {
  /**
   * Always a `[data-help="…"]` attribute selector, never a class or text match.
   * The attribute is the contract between the guide and the page.
   */
  selector: string;
  /** Short action phrase — "Filter benefits by category", not "Category filter". */
  title: string;
  /** 1–4 plain sentences: what it does, when to use it, what happens next. */
  body: string;
  /**
   * Groups the card in the drawer. Order comes from `PageHelp.sections`; a step with an
   * unlisted (or missing) section falls into a trailing "More" group.
   */
  section?: string;
  /** Hide the step from lower roles. Default: `employee` (everybody). */
  minRole?: HelpRole;
  /**
   * The element only exists after a UI action — a key in `PageHelp.contexts`, whose value
   * tells the reader how to get there. The tour skips these while they are off screen; the
   * drawer still lists them, with the instruction attached.
   */
  context?: string;
  /** The element needs data to exist (a non-empty list, an enrolled package, a dependent). */
  optional?: boolean;
  /** Translations of `title` / `body`. EN is this object's own fields. */
  i18n?: HelpStepI18n;
}

export interface PageHelp {
  title: string;
  /** 1–3 sentences: what the page is for and where it sits in the workflow. */
  intro: string;
  /** Display order for `HelpStep.section`. Sections not listed here render last. */
  sections?: string[];
  /** `HelpStep.context` key → how the reader reveals that part of the UI. */
  contexts?: Record<string, string>;
  /** Cross-cutting notes that belong to no single control. */
  tips?: string[];
  steps: HelpStep[];
  /** Translations of the page-level prose. Steps carry their own. */
  i18n?: PageHelpI18n;
}

/**
 * The prose to show, in the reader's language, with EN standing in per field where nothing
 * has been written yet. `translated` is false when ANY displayed field fell back.
 */
export interface LocalizedText<T> {
  value: T;
  translated: boolean;
}

const pick = <T>(translation: T | undefined, base: T): LocalizedText<T> =>
  translation === undefined || translation === null || translation === ""
    ? { value: base, translated: false }
    : { value: translation, translated: true };

export const localizedStep = (
  step: HelpStep,
  language: HelpLanguage
): { title: string; body: string; translated: boolean } => {
  if (language === HELP_BASE_LANGUAGE) {
    return { title: step.title, body: step.body, translated: true };
  }
  const source = step.i18n?.[language as HelpTranslatedLanguage];
  const title = pick(source?.title, step.title);
  const body = pick(source?.body, step.body);
  return { title: title.value, body: body.value, translated: title.translated && body.translated };
};

export const localizedPage = (
  help: PageHelp,
  language: HelpLanguage
): { title: string; intro: string; tips: string[] | undefined; translated: boolean } => {
  if (language === HELP_BASE_LANGUAGE) {
    return { title: help.title, intro: help.intro, tips: help.tips, translated: true };
  }
  const source = help.i18n?.[language as HelpTranslatedLanguage];
  const title = pick(source?.title, help.title);
  const intro = pick(source?.intro, help.intro);
  const tips = pick(source?.tips?.length ? source.tips : undefined, help.tips);
  return {
    title: title.value,
    intro: intro.value,
    tips: tips.value,
    translated: title.translated && intro.translated && (!help.tips?.length || tips.translated),
  };
};

/** A section heading is an id in EN and a label in every language, hence keyed by the EN name. */
export const localizedSection = (
  help: Pick<PageHelp, "i18n">,
  section: string,
  language: HelpLanguage
): string =>
  language === HELP_BASE_LANGUAGE
    ? section
    : (help.i18n?.[language as HelpTranslatedLanguage]?.sections?.[section] ?? section);

export const localizedContext = (
  help: Pick<PageHelp, "i18n" | "contexts">,
  key: string,
  language: HelpLanguage
): string | undefined =>
  (language === HELP_BASE_LANGUAGE
    ? undefined
    : help.i18n?.[language as HelpTranslatedLanguage]?.contexts?.[key]) ?? help.contexts?.[key];

/** The whole `contexts` map in one language, for a surface that is handed the record itself. */
export const localizedContexts = (
  help: PageHelp,
  language: HelpLanguage
): Record<string, string> | undefined => {
  if (!help.contexts) return undefined;
  if (language === HELP_BASE_LANGUAGE) return help.contexts;
  const translated = help.i18n?.[language as HelpTranslatedLanguage]?.contexts;
  if (!translated) return help.contexts;
  return Object.fromEntries(
    Object.entries(help.contexts).map(([key, value]) => [key, translated[key] ?? value])
  );
};

/** One rank per tier in `HelpRole`, lowest first. */
export const ROLE_RANK: Record<HelpRole, number> = {
  employee: 0,
};

/** Maps the viewer flags onto the ranks above — a single tier today. */
export const rankForViewer = (_viewer: HelpViewer): number => 0;

export const stepVisibleForRole = (step: HelpStep, viewer: HelpViewer): boolean =>
  rankForViewer(viewer) >= ROLE_RANK[step.minRole ?? "employee"];

/** The role badge shown on a card, or `null` when the step is visible to everyone. */
export const roleBadgeLabel = (_step: HelpStep): string | null => null;

/**
 * Steps grouped for the drawer, in `PageHelp.sections` order. Sections present on steps but
 * missing from that list keep their first-seen order and land after the declared ones, so
 * adding a step without touching `sections` degrades to "shown last" rather than "dropped".
 *
 * Takes the section list rather than the whole `PageHelp` because the tour groups too — and the
 * tour only ever holds the on-screen subset of the steps.
 */
export const groupStepsBySection = (
  sections: string[] | undefined,
  steps: HelpStep[]
): { section: string; steps: HelpStep[] }[] => {
  const FALLBACK = "More";
  const buckets = new Map<string, HelpStep[]>();
  for (const section of sections ?? []) buckets.set(section, []);
  for (const step of steps) {
    const key = step.section ?? FALLBACK;
    const bucket = buckets.get(key);
    if (bucket) bucket.push(step);
    else buckets.set(key, [step]);
  }
  return Array.from(buckets.entries())
    .filter(([, group]) => group.length > 0)
    .map(([section, group]) => ({ section, steps: group }));
};

/**
 * The anchor name behind a step's selector — `[data-help="benefits-category-filter"]` →
 * `benefits-category-filter`. What "point at a field" runs on: it starts from an element, reads
 * its attribute, and has to find the step that documents it.
 */
export const anchorNameOf = (selector: string): string | null =>
  /^\[data-help="([^"]+)"\]$/.exec(selector)?.[1] ?? null;

/**
 * Free-text match for the drawer's search box and the tour's jump list.
 *
 * Every whitespace-separated term must appear somewhere in the step, in any order, and the BODY
 * is searched too: a user who cannot name the field ("the one about my salary") is exactly the
 * user who needs the search. Every language's text is searched, so an untranslated step does not
 * vanish the moment another language is selected.
 */
export const stepMatchesQuery = (step: HelpStep, query: string): boolean => {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const translations = Object.values(step.i18n ?? {})
    .map((t) => `${t?.title ?? ""} ${t?.body ?? ""}`)
    .join(" ");
  const haystack =
    `${step.title} ${step.body} ${step.section ?? ""} ${step.selector} ${translations}`.toLowerCase();
  return terms.every((term) => haystack.includes(term));
};
